import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../../../src/components/services/axiosInstance.js';
import {
    Typography,
    Container,
    Box,
    Button,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    CircularProgress,
    Tab,
    Tabs,
    Alert
} from '@mui/material';

const SendInvitationForm = ({ assessments = [], userId }) => {
    const { t } = useTranslation();

    const [checkpoints, setCheckpoints] = useState([]);
    const [invitationData, setInvitationData] = useState({
        assessment_id: '',
        checkpoint_id: userId,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [invitationLink, setInvitationLink] = useState('');
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        const fetchCheckpoints = async () => {
            setLoading(true);
            try {
                const { data } = await axiosInstance.get('/api/onboarding/checkpoints');
                setCheckpoints(data);
            } catch (err) {
                console.error('❌ Error fetching checkpoints:', err);
                setError(t('error_fetch_checkpoints'));
            } finally {
                setLoading(false);
            }
        };
        fetchCheckpoints();
    }, [t]);

    const handleTabChange = (_, newTab) => {
        setActiveTab(newTab);
    };

    const handleChange = (field) => (event) => {
        setInvitationData({ ...invitationData, [field]: event.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { assessment_id, checkpoint_id } = invitationData;

        if (!assessment_id || !checkpoint_id) {
            setError(t('error_fill_fields'));
            return;
        }

        try {
            setError('');
            setSaving(true);
            const invitationId = await saveInvitation(assessment_id, checkpoint_id);
            if (invitationId) {
                const link = generateInvitationLink(invitationId);
                setInvitationLink(link);
                alert(t('success_invitation'));
            }
        } finally {
            setSaving(false);
        }
    };

    const saveInvitation = async (assessment_id, checkpoint_id) => {
        try {
            const { data } = await axiosInstance.post(
                `/api/invitation/assessment_requests/${assessment_id}/invite`,
                { checkpoint_id }
            );

            if (data?.invitation_id) {
                return data.invitation_id;
            }

            console.error(t('error_invalid_response'), data);
            setError(t('error_saving_invitation'));
        } catch (err) {
            console.error(t('error_saving_invitation'), err);
            setError(err?.response?.data?.message || t('error_unexpected'));
        }
        return null;
    };

    const generateInvitationLink = (id) => `${window.location.origin}/invite?id=${id}`;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(invitationLink)
            .then(() => alert(t('success_copy_link')))
            .catch(err => console.error(t('error_copy_link'), err));
    };

    const renderInvitationForm = () => (
        <Box sx={{ boxShadow: 3, borderRadius: '8px', p: 3, backgroundColor: '#fff' }}>
            {error && (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => setError('')}>
                            {t('dismiss')}
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {invitationLink && (
                <Box>
                    <Typography color="primary" sx={{ mb: 1 }}>
                        {t('invitation_link')}: <a href={invitationLink} target="_blank" rel="noopener noreferrer">{invitationLink}</a>
                    </Typography>
                    <Button variant="outlined" onClick={copyToClipboard} sx={{ mt: 1 }}>
                        {t('copy_link')}
                    </Button>
                </Box>
            )}

            <FormControl fullWidth margin="normal">
                <InputLabel>{t('assessment_id')}</InputLabel>
              <Select
                name="assessment_id"
                value={invitationData.assessment_id || ''}
                onChange={(e) => setInvitationData({ ...invitationData, assessment_id: e.target.value })}
                required
                >

                    {assessments.map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                            {a.item_details?.title || t('no_title')}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" disabled={loading}>
                <InputLabel>{t('checkpoint')}</InputLabel>
                <Select
                name="checkpoint_id"
                value={invitationData.checkpoint_id || ''}
                onChange={(e) => setInvitationData({ ...invitationData, checkpoint_id: e.target.value })}
                required
                disabled={loading}
                >

                    {loading ? (
                        <MenuItem disabled>
                            <CircularProgress size={24} />
                        </MenuItem>
                    ) : checkpoints.length > 0 ? (
                        checkpoints.map((cp) => (
                            <MenuItem key={cp.id} value={cp.userId}>
                                {cp.company_name} – {cp.city}, {cp.street}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>{t('no_checkpoints_available')}</MenuItem>
                    )}
                </Select>
            </FormControl>

            <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || saving}
                sx={{ mt: 2 }}
            >
                {saving ? <CircularProgress size={24} /> : t('send_invitation')}
            </Button>
        </Box>
    );

    const renderInstructions = () => (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>{t('instructions')}</Typography>
            <Typography>
                {t('instruction_1')}<br />
                {t('instruction_2')}<br />
                {t('instruction_3')}<br />
                {t('instruction_4')}
            </Typography>
        </Box>
    );

    return (
        <Container sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                {t('dashboard.send_invitation')}
            </Typography>

            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab label={t('invite_form')} />
                <Tab label={t('instructions')} />
            </Tabs>

            {activeTab === 0 ? renderInvitationForm() : renderInstructions()}
        </Container>
    );
};

export default SendInvitationForm;
